import { useState, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, FileSpreadsheet, Upload, Download } from 'lucide-react';
import { Select } from '../inputs/Select';
import { Alert } from '../feedback/Alert';
import type { GuestTag } from '../../types';

/** Map user-facing labels and internal values to the internal rsvp_status. */
const RSVP_LABEL_MAP: Record<string, string> = {
  // Internal values
  pending: 'pending',
  yes: 'yes',
  no: 'no',
  maybe: 'maybe',
  // User-facing labels
  awaiting: 'pending',
  attending: 'yes',
  'not attending': 'no',
  notattending: 'no',
  declined: 'no',
};

const DIETARY_OPTIONS = ['vegetarian', 'pescatarian', 'vegan'] as const;
const ALLERGY_OPTIONS = ['nuts', 'dairy', 'gluten', 'shellfish', 'eggs', 'soy'] as const;

interface ParsedGuest {
  name: string;
  email?: string;
  tagNames?: string[];
  dietary_preference?: string;
  allergies?: string[];
  rsvp_status?: string;
}

interface BulkImportModalProps {
  onClose: () => void;
  onImport: (data: { inviteId?: number; guests: { name: string; email?: string; tag_ids?: number[]; dietary_preference?: string; allergies?: string[]; rsvp_status?: string }[] }) => Promise<void>;

  isImporting: boolean;
  inviteOptions?: { value: string; label: string }[];
  tags?: GuestTag[];
  onCreateTag?: (name: string) => Promise<GuestTag>;
  /** When provided the Export tab is available. */
  onExport?: () => void;
  /** Number of guests that will be exported — shown in the Export tab. */
  exportGuestCount?: number;
  /** Which tab to show first. Defaults to 'import'. */
  initialMode?: 'import' | 'export';
}

/** Parse a single CSV line respecting quoted fields. */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current.trim());
  return fields;
}

interface ColumnMap {
  nameIdx: number;
  emailIdx: number;
  tagsIdx: number;
  dietaryIdx: number;
  allergiesIdx: number;
  rsvpIdx: number;
}

/** Detect column indices from a header row. */
function detectColumns(headers: string[]): ColumnMap {
  const lower = headers.map(h => h.toLowerCase().replace(/[^a-z]/g, ''));
  let nameIdx = lower.findIndex(h => h === 'name' || h === 'fullname' || h === 'guestname');
  let emailIdx = lower.findIndex(h => h === 'email' || h === 'emailaddress' || h === 'mail');
  const tagsIdx = lower.findIndex(h => h === 'tags' || h === 'tag' || h === 'group' || h === 'groups');
  const dietaryIdx = lower.findIndex(h => h === 'dietary' || h === 'dietarypreference' || h === 'diet' || h === 'mealpreference');
  const allergiesIdx = lower.findIndex(h => h === 'allergies' || h === 'allergy');
  const rsvpIdx = lower.findIndex(h => h === 'rsvp' || h === 'rsvpstatus' || h === 'status' || h === 'attending');

  if (nameIdx === -1) nameIdx = 0;
  if (emailIdx === -1 && headers.length > 1) emailIdx = 1;

  return { nameIdx, emailIdx, tagsIdx, dietaryIdx, allergiesIdx, rsvpIdx };
}

/** Check if a row looks like a header. */
function isHeaderRow(fields: string[]): boolean {
  const joined = fields.join(' ').toLowerCase();
  return joined.includes('name') || joined.includes('email');
}

export function BulkImportModal({ onClose, onImport, isImporting, inviteOptions, tags, onCreateTag, onExport, exportGuestCount = 0, initialMode = 'import' }: BulkImportModalProps) {
  const [mode, setMode] = useState<'import' | 'export'>(initialMode);
  const [selectedInviteId, setSelectedInviteId] = useState('');
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResolvingTags, setIsResolvingTags] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseGuestsFromText = useCallback((text: string): ParsedGuest[] => {
    const lines = text.trim().split('\n').filter(Boolean);
    if (lines.length === 0) return [];

    const allFields = lines.map(parseCsvLine);
    let startIdx = 0;
    let cols: ColumnMap = { nameIdx: 0, emailIdx: 1, tagsIdx: -1, dietaryIdx: -1, allergiesIdx: -1, rsvpIdx: -1 };

    if (allFields.length > 1 && isHeaderRow(allFields[0])) {
      cols = detectColumns(allFields[0]);
      startIdx = 1;
    }

    return allFields.slice(startIdx).map(fields => {
      const tagStr = cols.tagsIdx >= 0 ? fields[cols.tagsIdx] : undefined;
      const tagNames = tagStr
        ? tagStr.split(';').map(t => t.trim()).filter(Boolean)
        : undefined;

      const rawRsvp = cols.rsvpIdx >= 0 ? fields[cols.rsvpIdx]?.toLowerCase().trim() : undefined;
      const rsvp_status = rawRsvp ? RSVP_LABEL_MAP[rawRsvp] : undefined;

      // Match dietary preference against known options (case-insensitive), keep as-is for "Other"
      const rawDietary = cols.dietaryIdx >= 0 ? fields[cols.dietaryIdx]?.trim() : undefined;
      let dietary_preference: string | undefined;
      if (rawDietary) {
        const matched = DIETARY_OPTIONS.find(o => o === rawDietary.toLowerCase());
        // Capitalise first letter to match system format (Vegetarian, Vegan, etc.)
        dietary_preference = matched
          ? matched.charAt(0).toUpperCase() + matched.slice(1)
          : rawDietary; // treat as "Other" custom value
      }

      // Parse allergies — semicolon-separated, match known ones (case-insensitive), keep custom
      const rawAllergies = cols.allergiesIdx >= 0 ? fields[cols.allergiesIdx]?.trim() : undefined;
      let allergies: string[] | undefined;
      if (rawAllergies) {
        allergies = rawAllergies.split(';').map(a => {
          const trimmed = a.trim();
          const matched = ALLERGY_OPTIONS.find(o => o === trimmed.toLowerCase());
          // Capitalise to match system format (Nuts, Dairy, etc.)
          return matched
            ? matched.charAt(0).toUpperCase() + matched.slice(1)
            : trimmed;
        }).filter(Boolean);
        if (!allergies.length) allergies = undefined;
      }

      return {
        name: fields[cols.nameIdx] || '',
        email: cols.emailIdx >= 0 && fields[cols.emailIdx] ? fields[cols.emailIdx] : undefined,
        tagNames: tagNames?.length ? tagNames : undefined,
        dietary_preference,
        allergies,
        rsvp_status,
      };
    }).filter(g => g.name);
  }, []);

  const handleFileContent = useCallback((text: string, name: string) => {
    setCsvText(text);
    setFileName(name);
    setError(null);
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.name.match(/\.(csv|txt|tsv)$/i)) {
      setError('Please upload a CSV file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) handleFileContent(text, file.name);
    };
    reader.readAsText(file);
  }, [handleFileContent]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const parsedGuests = useMemo(
    () => (csvText.trim() ? parseGuestsFromText(csvText) : []),
    [csvText, parseGuestsFromText]
  );

  const uniqueTagNames = useMemo(() => {
    const names = new Set<string>();
    for (const g of parsedGuests) {
      g.tagNames?.forEach(t => names.add(t));
    }
    return Array.from(names);
  }, [parsedGuests]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (inviteOptions && !selectedInviteId) {
      setError('Please select an invite');
      return;
    }

    if (parsedGuests.length === 0) {
      setError('Please enter at least one guest');
      return;
    }

    try {
      // Resolve tag names to IDs, creating any that don't exist
      let tagNameToId: Map<string, number> | undefined;

      if (uniqueTagNames.length > 0 && onCreateTag) {
        setIsResolvingTags(true);
        tagNameToId = new Map<string, number>();

        // Build lookup from existing tags (case-insensitive)
        const existing = new Map<string, number>();
        if (tags) {
          for (const tag of tags) {
            existing.set(tag.name.toLowerCase(), tag.id);
          }
        }

        for (const name of uniqueTagNames) {
          const existingId = existing.get(name.toLowerCase());
          if (existingId) {
            tagNameToId.set(name.toLowerCase(), existingId);
          } else {
            const newTag = await onCreateTag(name);
            tagNameToId.set(name.toLowerCase(), newTag.id);
          }
        }
        setIsResolvingTags(false);
      }

      const guests = parsedGuests.map(g => ({
        name: g.name,
        email: g.email,
        tag_ids: g.tagNames && tagNameToId
          ? g.tagNames.map(t => tagNameToId!.get(t.toLowerCase())).filter((id): id is number => id != null)
          : undefined,
        dietary_preference: g.dietary_preference,
        allergies: g.allergies,
        rsvp_status: g.rsvp_status,
      }));

      await onImport({
        inviteId: selectedInviteId ? Number(selectedInviteId) : undefined,
        guests,
      });
      onClose();
    } catch (err: any) {
      setIsResolvingTags(false);
      setError(err.response?.data?.message || 'Failed to import guests');
    }
  };

  const handleDownloadTemplate = useCallback(() => {
    const csv = [
      'Name,Email,Tags,RSVP,Dietary,Allergies',
      'Jane Smith,jane@example.com,Family; Bride\'s Side,Attending,Vegetarian,Nuts; Dairy',
      'John Doe,john@example.com,Friends,Awaiting,Vegan,',
      'Sarah Wilson,,Work; Groom\'s Side,Maybe,,Gluten; Shellfish',
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'guest-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const isBusy = isImporting || isResolvingTags;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-lg bg-background-card rounded-xl border border-border shadow-xl mx-4 flex flex-col max-h-[90dvh]"
      >
        {/* Header with pill toggle */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-1 bg-background-elevated border border-border rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setMode('import')}
              data-builder="modal-import-tab"
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mode === 'import'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              Import
            </button>
            <button
              type="button"
              onClick={() => setMode('export')}
              disabled={!onExport}
              data-builder="modal-export-tab"
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mode === 'export'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Export
            </button>
          </div>
          <button onClick={onClose} data-builder="modal-import-close" className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors">
            <X size={20} className="text-text-secondary" />
          </button>
        </div>

        {mode === 'export' ? (
          /* ─── Export tab ─── */
          <div className="flex flex-col flex-1 min-h-0">
            <div className="overflow-y-auto flex-1 p-6">
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Download size={24} className="text-primary" />
                </div>
                <div className="text-center">
                  {exportGuestCount > 0 ? (
                    <>
                      <p className="text-sm font-medium text-text-primary">
                        {exportGuestCount} guest{exportGuestCount !== 1 ? 's' : ''} ready to export
                      </p>
                      <p className="text-xs text-text-secondary mt-1">
                        Includes name, email, invite, RSVP, dietary, allergies, tags, and more
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-text-secondary">No guests to export yet</p>
                  )}
                </div>
              </div>
            </div>
            <div className="shrink-0 flex gap-3 px-6 py-3 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                data-builder="modal-export-cancel"
                className="flex-1 px-4 py-2 text-sm border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={exportGuestCount === 0}
                onClick={() => { onExport?.(); onClose(); }}
                data-builder="modal-export-download"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
              >
                <Download size={16} />
                Download CSV
              </button>
            </div>
          </div>
        ) : (
          /* ─── Import tab ─── */
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-4">
            {inviteOptions && (
              <Select
                label="Invite"
                options={inviteOptions}
                value={selectedInviteId}
                onChange={setSelectedInviteId}
                placeholder="Select an invite..."
              />
            )}

            {/* File upload area */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              data-builder="modal-import-upload-area"
              className={`flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-background-elevated/50'
              }`}
            >
              <Upload size={20} className="text-text-secondary" />
              {fileName ? (
                <p className="text-sm text-text-primary font-medium">{fileName}</p>
              ) : (
                <p className="text-sm text-text-secondary">
                  Drop a CSV file here or <span className="text-primary">browse</span>
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.tsv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
            </div>

            {/* Format guide */}
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs text-text-secondary leading-relaxed">
                Supported columns: <span className="font-medium text-text-primary">Name</span> (required), <span className="font-medium text-text-primary">Email</span>, <span className="font-medium text-text-primary">Tags</span>, <span className="font-medium text-text-primary">RSVP</span> (Attending, Not Attending, Maybe, Awaiting), <span className="font-medium text-text-primary">Dietary</span> (Vegetarian, Pescatarian, Vegan), <span className="font-medium text-text-primary">Allergies</span> (Nuts, Dairy, Gluten, Shellfish, Eggs, Soy). Use semicolons for multiple tags or allergies. Header row auto-detected.
              </p>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                data-builder="modal-import-template-btn"
                className="shrink-0 inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover transition-colors whitespace-nowrap"
              >
                <Download size={12} />
                Template
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-text-secondary">or paste below</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div>
              <textarea
                value={csvText}
                onChange={(e) => { setCsvText(e.target.value); setFileName(null); }}
                placeholder="Name, Email, Tags, RSVP, Dietary, Allergies&#10;John Smith, john@example.com, Family, Attending, Vegetarian, Nuts; Dairy&#10;Jane Doe, , Friends, Awaiting, ,&#10;Bob Wilson, bob@example.com, , Maybe, Vegan, Gluten"
                rows={6}
                data-builder="modal-import-csv-textarea"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              />
            </div>

            {/* Preview count */}
            {parsedGuests.length > 0 && (
              <div className="flex flex-col gap-1 text-sm text-text-secondary">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={14} />
                  {parsedGuests.length} guest{parsedGuests.length !== 1 ? 's' : ''} ready to import
                  {parsedGuests.filter(g => g.email).length > 0 && (
                    <span>({parsedGuests.filter(g => g.email).length} with email)</span>
                  )}
                </div>
                {uniqueTagNames.length > 0 && (
                  <div className="text-xs text-text-tertiary pl-6">
                    Tags: {uniqueTagNames.join(', ')}
                  </div>
                )}
              </div>
            )}

            {error && <Alert variant="error">{error}</Alert>}
          </div>

          <div className="shrink-0 flex gap-3 px-6 py-3 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              data-builder="modal-import-cancel"
              className="flex-1 px-4 py-2 text-sm border border-border rounded-lg text-text-secondary hover:bg-background-elevated transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isBusy || parsedGuests.length === 0}
              data-builder="modal-import-submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
            >
              {isBusy ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
              <span className="truncate">
                {isResolvingTags ? 'Creating tags...' : isImporting ? 'Importing...' : `Import${parsedGuests.length ? ` ${parsedGuests.length}` : ''} Guest${parsedGuests.length !== 1 ? 's' : ''}`}
              </span>
            </button>
          </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
